
import React from 'react';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import GradientButton, { GradientVariant } from './GradientButton';
import CardHeader from './CardHeader';
import { AlertTriangle } from 'lucide-react';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  isConfirmLoading?: boolean;
  isCancelLoading?: boolean;
  confirmVariant?: GradientVariant;
  cancelVariant?: GradientVariant;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isConfirmLoading = false,
  isCancelLoading = false,
  confirmVariant = 'blue',
  cancelVariant = 'green',
}) => {
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    onCancel();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] p-0 overflow-hidden">
        {/* Required DialogTitle for accessibility - visually hidden since CardHeader shows the title */}
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="p-6">
          <CardHeader 
            icon={AlertTriangle} 
            title={title}
            variant="red"
            className="mb-4"
          />
          <DialogDescription className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {description}
          </DialogDescription>
        {/* Override the default DialogFooter styles to create a true left-right layout */}
        <div className="flex flex-row justify-between items-center w-full mt-5">
          <div className="flex-1">
            <GradientButton
              variant="green"
              onClick={onCancel}
              isLoading={isCancelLoading}
              fullWidth={false}
              testId="confirm-dialog-cancel"
              className="min-w-[120px]"
            >
              {cancelText}
            </GradientButton>
          </div>
          <div className="flex-1 flex justify-end">
            <GradientButton
              variant={confirmVariant}
              onClick={onConfirm}
              isLoading={isConfirmLoading}
              fullWidth={false}
              testId="confirm-dialog-confirm"
              className="min-w-[120px]"
            >
              {confirmText}
            </GradientButton>
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
